import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  ApiError,
  type ConfigPiloto,
  type OndaPiloto,
  type PainelPiloto,
  type PlanejamentoPiloto,
} from '../api.js';
import { CATEGORY_NAME_BY_ID, SHOPEE_CATEGORIES } from '../categories.js';

/**
 * Piloto automático do WhatsApp.
 *
 * Todo dia, no horário de planejar, ele monta a fila do dia SEGUINTE em ondas
 * — escolhe os produtos das categorias pelo que está vendendo, gera o texto de
 * cada um e agenda espaçado. Fica na Agenda porque o resultado dele é fila: os
 * posts aparecem logo abaixo, editáveis e canceláveis um a um, e cada
 * planejamento tem o seu "cancelar tudo" como freio de mão.
 */

const nomeCategoria = (id: number) => CATEGORY_NAME_BY_ID.get(id) ?? `categoria ${id}`;

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** `day` é data pura (meia-noite UTC): formatar em UTC, senão vira o dia anterior. */
const diaCurto = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', {
    timeZone: 'UTC',
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });

/** Segundos em minutos para exibir: 120 → "2", 150 → "2,5". */
const emMinutos = (seg: number) => String(Math.round((seg / 60) * 10) / 10).replace('.', ',');

function mensagemDe(cause: unknown): string {
  if (cause instanceof ApiError && cause.issues?.length) {
    return cause.issues.map((i) => i.message).join(' · ');
  }
  return cause instanceof Error ? cause.message : 'Algo deu errado';
}

/** "10–15" quando todas as ondas pedem o mesmo; senão, por onda. */
function faixaDePosts(ondas: OndaPiloto[]): string {
  const faixa = (o: OndaPiloto) => (o.min === o.max ? `${o.min}` : `${o.min}–${o.max}`);
  const todas = new Set(ondas.map(faixa));
  return todas.size === 1 ? `${[...todas][0]} posts por onda` : ondas.map(faixa).join(' / ') + ' posts';
}

export function AutopilotPanel({ onPlanejado }: { onPlanejado: () => void }) {
  const [painel, setPainel] = useState<PainelPiloto | null>(null);
  const [form, setForm] = useState<ConfigPiloto | null>(null);
  const [aberto, setAberto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);

  // A Agenda recria o callback a cada recarga (30s); guardar em ref evita
  // reiniciar o acompanhamento do planejamento a cada vez.
  const aoPlanejar = useRef(onPlanejado);
  aoPlanejar.current = onPlanejado;

  const carregar = useCallback(async () => {
    const p = await api.piloto();
    setPainel(p);
    return p;
  }, []);

  useEffect(() => {
    void carregar()
      .then((p) => setForm(p.config))
      .catch(() => undefined);
  }, [carregar]);

  // Enquanto o piloto planeja, acompanha até terminar e recarrega a fila.
  useEffect(() => {
    if (!painel?.planejando) return;
    const timer = setInterval(() => {
      void carregar()
        .then((p) => {
          if (!p.planejando) aoPlanejar.current();
        })
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(timer);
  }, [painel?.planejando, carregar]);

  if (!painel || !form) return null;
  const cfg = painel.config;

  async function salvar(proximo: ConfigPiloto, textoOk: string) {
    setOcupado(true);
    setAviso(null);
    try {
      const salvo = await api.salvarPiloto(proximo);
      setForm(salvo);
      await carregar();
      setAviso({ ok: true, texto: textoOk });
    } catch (cause) {
      setAviso({ ok: false, texto: mensagemDe(cause) });
    } finally {
      setOcupado(false);
    }
  }

  async function planejar(dia: 'hoje' | 'amanha') {
    setAviso(null);
    try {
      await api.planejarPiloto(dia);
      await carregar();
    } catch (cause) {
      setAviso({ ok: false, texto: mensagemDe(cause) });
    }
  }

  async function cancelar(run: PlanejamentoPiloto) {
    const ok = window.confirm(
      `Cancelar os ${run.pendentes} post(s) deste planejamento que ainda não saíram?\n\n` +
        'O que já foi publicado fica no histórico, e o que você agendou à mão não é tocado.',
    );
    if (!ok) return;
    try {
      const r = await api.cancelarPlanejamento(run.id);
      setAviso({ ok: true, texto: `${r.cancelados} post(s) cancelado(s).` });
      await carregar();
      aoPlanejar.current();
    } catch (cause) {
      setAviso({ ok: false, texto: mensagemDe(cause) });
    }
  }

  const patch = (p: Partial<ConfigPiloto>) => setForm((f) => (f ? { ...f, ...p } : f));
  const patchOnda = (i: number, p: Partial<OndaPiloto>) =>
    patch({ waves: form.waves.map((o, j) => (j === i ? { ...o, ...p } : o)) });
  const naoEscolhidas = SHOPEE_CATEGORIES.filter((c) => !form.categories.includes(c.id));

  return (
    <section className="card" style={{ padding: 16, marginBottom: 18 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span aria-hidden="true">🤖</span>
        <p className="section-title" style={{ margin: 0, flex: 1, minWidth: 180 }}>
          Piloto automático do WhatsApp
        </p>
        <span className={`badge ${cfg.enabled ? 'ok' : ''}`} style={{ flex: '0 0 auto' }}>
          {painel.planejando ? 'planejando…' : cfg.enabled ? 'ligado' : 'desligado'}
        </span>
        <button
          type="button"
          className={cfg.enabled ? '' : 'primary'}
          style={{ flex: '0 0 auto' }}
          disabled={ocupado}
          onClick={() =>
            void salvar(
              { ...cfg, enabled: !cfg.enabled },
              cfg.enabled ? 'Piloto desligado.' : 'Piloto ligado.',
            )
          }
        >
          {cfg.enabled ? 'Desligar' : 'Ligar'}
        </button>
        <button
          type="button"
          className="ghost"
          style={{ flex: '0 0 auto' }}
          aria-expanded={aberto}
          onClick={() => {
            setForm(cfg);
            setAberto((v) => !v);
          }}
        >
          {aberto ? 'fechar' : 'configurar'}
        </button>
      </div>

      {/* A frase que responde "o que ele vai fazer" sem abrir nada. */}
      <p className="muted" style={{ margin: '8px 0 0', fontSize: 12.5 }}>
        Todo dia às <strong>{cfg.planAt}</strong> planeja o dia seguinte: ondas às{' '}
        <strong>{cfg.waves.map((w) => w.inicio).join(', ')}</strong>, {faixaDePosts(cfg.waves)}, a
        cada {emMinutos(cfg.intervalMinSec)} a {emMinutos(cfg.intervalMaxSec)} min ·{' '}
        {cfg.categories.map(nomeCategoria).join(', ')} · sem repetir produto por{' '}
        {cfg.repeatAfterDays} dias.
        {cfg.enabled && painel.proximoPlanejamento && (
          <> Próximo planejamento: {new Date(painel.proximoPlanejamento).toLocaleString('pt-BR', {
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}.</>
        )}
      </p>

      {aberto && (
        <div className="form-grid" style={{ gap: 14, marginTop: 14 }}>
          <div>
            <label>Ondas do dia</label>
            <div style={{ display: 'grid', gap: 6 }}>
              {form.waves.map((o, i) => (
                <div key={i} className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="time"
                    value={o.inicio}
                    aria-label={`Início da onda ${i + 1}`}
                    onChange={(e) => patchOnda(i, { inicio: e.target.value })}
                    style={{ width: 116, flex: '0 0 auto' }}
                  />
                  <span className="muted" style={{ flex: '0 0 auto' }}>de</span>
                  <input
                    type="number"
                    min={1}
                    max={40}
                    value={o.min}
                    aria-label={`Mínimo de posts da onda ${i + 1}`}
                    onChange={(e) => patchOnda(i, { min: Number(e.target.value) })}
                    style={{ width: 70, flex: '0 0 auto' }}
                  />
                  <span className="muted" style={{ flex: '0 0 auto' }}>a</span>
                  <input
                    type="number"
                    min={1}
                    max={40}
                    value={o.max}
                    aria-label={`Máximo de posts da onda ${i + 1}`}
                    onChange={(e) => patchOnda(i, { max: Number(e.target.value) })}
                    style={{ width: 70, flex: '0 0 auto' }}
                  />
                  <span className="muted" style={{ flex: '0 0 auto' }}>posts</span>
                  {form.waves.length > 1 && (
                    <button
                      type="button"
                      className="ghost"
                      style={{ flex: '0 0 auto' }}
                      aria-label={`Remover a onda das ${o.inicio}`}
                      onClick={() => patch({ waves: form.waves.filter((_, j) => j !== i) })}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {form.waves.length < 6 && (
              <button
                type="button"
                className="ghost"
                style={{ marginTop: 6 }}
                onClick={() => patch({ waves: [...form.waves, { inicio: '21:00', min: 10, max: 15 }] })}
              >
                + onda
              </button>
            )}
          </div>

          <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              <label htmlFor="piloto-int-min">Entre um post e outro (minutos)</label>
              <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                <input
                  id="piloto-int-min"
                  type="number"
                  min={1}
                  step={0.5}
                  value={form.intervalMinSec / 60}
                  onChange={(e) => patch({ intervalMinSec: Math.round(Number(e.target.value) * 60) })}
                  style={{ width: 80, flex: '0 0 auto' }}
                />
                <span className="muted" style={{ flex: '0 0 auto' }}>a</span>
                <input
                  type="number"
                  min={1}
                  step={0.5}
                  aria-label="Intervalo máximo, em minutos"
                  value={form.intervalMaxSec / 60}
                  onChange={(e) => patch({ intervalMaxSec: Math.round(Number(e.target.value) * 60) })}
                  style={{ width: 80, flex: '0 0 auto' }}
                />
              </div>
            </div>
            <div style={{ flex: '0 1 160px' }}>
              <label htmlFor="piloto-plan">Planeja o dia seguinte às</label>
              <input
                id="piloto-plan"
                type="time"
                value={form.planAt}
                onChange={(e) => patch({ planAt: e.target.value })}
              />
            </div>
            <div style={{ flex: '0 1 160px' }}>
              <label htmlFor="piloto-rep">Não repetir produto por</label>
              <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                <input
                  id="piloto-rep"
                  type="number"
                  min={1}
                  max={90}
                  value={form.repeatAfterDays}
                  onChange={(e) => patch({ repeatAfterDays: Number(e.target.value) })}
                  style={{ width: 80, flex: '0 0 auto' }}
                />
                <span className="muted" style={{ flex: '0 0 auto' }}>dias</span>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="piloto-cat">Categorias</label>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {form.categories.map((id) => (
                <span key={id} className="badge accent" style={{ flex: '0 0 auto' }}>
                  {nomeCategoria(id)}
                  {form.categories.length > 1 && (
                    <button
                      type="button"
                      className="ghost"
                      style={{ padding: '0 0 0 6px', fontSize: 11 }}
                      aria-label={`Tirar ${nomeCategoria(id)}`}
                      onClick={() => patch({ categories: form.categories.filter((c) => c !== id) })}
                    >
                      ✕
                    </button>
                  )}
                </span>
              ))}
              {naoEscolhidas.length > 0 && (
                <select
                  id="piloto-cat"
                  value=""
                  onChange={(e) =>
                    e.target.value && patch({ categories: [...form.categories, Number(e.target.value)] })
                  }
                  style={{ width: 'auto', flex: '0 0 auto' }}
                >
                  <option value="">+ categoria</option>
                  {naoEscolhidas.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <button
            type="button"
            className="primary"
            style={{ maxWidth: 220 }}
            disabled={ocupado}
            onClick={() => void salvar(form, 'Configuração salva.')}
          >
            {ocupado ? <span className="spinner" /> : 'Salvar configuração'}
          </button>
        </div>
      )}

      <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={{ flex: '0 0 auto' }}
          disabled={painel.planejando}
          onClick={() => void planejar('amanha')}
        >
          Planejar amanhã agora
        </button>
        <button
          type="button"
          className="ghost"
          style={{ flex: '0 0 auto' }}
          disabled={painel.planejando}
          onClick={() => void planejar('hoje')}
        >
          Planejar o resto de hoje
        </button>
        {painel.planejando && (
          <span className="muted" style={{ fontSize: 12.5, alignSelf: 'center' }}>
            <span className="spinner" /> escolhendo produtos e escrevendo os textos — leva um ou dois
            minutos
          </span>
        )}
      </div>

      {aviso && (
        <p
          className={aviso.ok ? 'muted' : 'err'}
          style={{ margin: '8px 0 0', fontSize: 12.5, color: aviso.ok ? 'var(--gain)' : undefined }}
        >
          {aviso.texto}
        </p>
      )}

      {painel.runs.length > 0 && (
        <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
          {painel.runs.slice(0, 4).map((r) => (
            <PlanejamentoLinha key={r.id} run={r} onCancelar={() => void cancelar(r)} />
          ))}
        </div>
      )}
    </section>
  );
}

function PlanejamentoLinha({
  run,
  onCancelar,
}: {
  run: PlanejamentoPiloto;
  onCancelar: () => void;
}) {
  const emAndamento = !run.finishedAt;
  const s = run.summary;
  return (
    <div
      style={{
        padding: '10px 12px',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-control)',
        fontSize: 12.5,
      }}
    >
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ flex: '0 0 auto' }}>{diaCurto(run.day)}</strong>
        <span className="muted" style={{ flex: '0 0 auto' }}>
          {run.trigger === 'agenda' ? 'automático' : 'manual'}
        </span>
        <span
          className={`badge ${emAndamento ? 'warn' : run.ok ? 'ok' : 'err'}`}
          style={{ flex: '0 0 auto' }}
        >
          {emAndamento ? 'planejando' : run.ok ? `${run.created} post(s)` : 'falhou'}
        </span>
        {!emAndamento && (
          <span className="muted" style={{ flex: 1, minWidth: 160 }}>
            {run.publicados} publicado(s) · {run.pendentes} na fila
            {run.cancelados > 0 && ` · ${run.cancelados} cancelado(s)`}
            {run.falhas > 0 && ` · ${run.falhas} com falha`}
          </span>
        )}
        {run.pendentes > 0 && (
          <button type="button" className="danger" style={{ flex: '0 0 auto' }} onClick={onCancelar}>
            Cancelar {run.pendentes} pendente(s)
          </button>
        )}
      </div>

      {s.ondas && s.ondas.length > 0 && (
        <div className="muted" style={{ marginTop: 6 }}>
          {s.ondas
            .map((o) =>
              o.pulada
                ? `${o.inicio}: pulada — ${o.pulada}`
                : `${o.inicio}: ${o.criados} post(s)` +
                  (o.primeiro && o.ultimo ? `, ${hora(o.primeiro)}–${hora(o.ultimo)}` : ''),
            )
            .join(' · ')}
        </div>
      )}
      {s.porCategoria && Object.keys(s.porCategoria).length > 0 && (
        <div className="muted" style={{ marginTop: 4 }}>
          {Object.entries(s.porCategoria)
            .map(([id, n]) => `${nomeCategoria(Number(id))} ${n}`)
            .join(' · ')}
          {typeof s.candidatos === 'number' && ` · ${s.candidatos} produto(s) elegíveis`}
        </div>
      )}
      {run.error && (
        <div className="notice err" style={{ marginTop: 6, marginBottom: 0, fontSize: 12 }}>
          {run.error}
        </div>
      )}
      {s.avisos?.map((a) => (
        <div key={a} className="notice warn" style={{ marginTop: 6, marginBottom: 0, fontSize: 12 }}>
          {a}
        </div>
      ))}
    </div>
  );
}
